import React, { useState, useEffect } from 'react';
import { GlobalSettings as GlobalSettingsType } from '../types/types';
import { apiService } from '../services/api';
import '../styles/GlobalSettings.css';

export const GlobalSettings: React.FC<{ readOnlyExceptLogLevel?: boolean }> = ({
  readOnlyExceptLogLevel = false,
}) => {
  const [settings, setSettings] = useState<GlobalSettingsType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [changedFields, setChangedFields] = useState<Partial<GlobalSettingsType>>({});
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [artificialDelayError, setArtificialDelayError] = useState<string | null>(null);
  const [readBatchSizeError, setReadBatchSizeError] = useState<string | null>(null);
  const [readDelayError, setReadDelayError] = useState<string | null>(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
  };

  const validateArtificialDelay = (value: string): boolean => {
    // Trim whitespace from the value
    value = value.trim();
    
    // Check if empty
    if (!value) {
      setArtificialDelayError('Value cannot be empty');
      return false;
    }

    const parts = value.split(',').map(part => part.trim());
    if (parts.length !== 3) {
      setArtificialDelayError('Format must be: enabled,minDelay,maxDelay');
      return false;
    }

    const [enabled, minDelay, maxDelay] = parts;
    
    // Validate enabled flag
    if (enabled.toLowerCase() !== 'true' && enabled.toLowerCase() !== 'false') {
      setArtificialDelayError('First value must be true or false');
      return false;
    }

    // Validate minDelay
    const minDelayNum = parseInt(minDelay);
    if (isNaN(minDelayNum) || minDelayNum < 0) {
      setArtificialDelayError('minDelay must be a positive number');
      return false;
    }

    // Validate maxDelay
    const maxDelayNum = parseInt(maxDelay);
    if (isNaN(maxDelayNum) || maxDelayNum < 0) {
      setArtificialDelayError('maxDelay must be a positive number');
      return false;
    }

    // Validate maxDelay >= minDelay
    if (maxDelayNum < minDelayNum) {
      setArtificialDelayError('maxDelay must be greater than or equal to minDelay');
      return false;
    }

    setArtificialDelayError(null);
    return true;
  };

  const fetchConfig = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getExtendedConfig();
      if (data && data.globalSettings) {
        setSettings(data.globalSettings);
        setChangedFields({});
      } else {
        setError('Invalid configuration data received');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Failed to load configuration. Please check if the server is running.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof GlobalSettingsType) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (!settings) return;
    if (readOnlyExceptLogLevel && field !== 'logLevel') return;
    
    const newValue = field === 'globalReadBatchSize' || field === 'globalReadDelayMillis'
      ? Number(event.target.value)
      : event.target.value;
      
    // Clear errors when user makes changes
    if (field === 'artificialDelaySetting') {
      setArtificialDelayError(null);
    } else if (field === 'globalReadBatchSize') {
      setReadBatchSizeError(null);
    } else if (field === 'globalReadDelayMillis') {
      setReadDelayError(null);
    }
      
    setSettings(prev => ({ ...prev!, [field]: newValue }));
    setChangedFields(prev => ({ ...prev, [field]: newValue }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!settings || isSaving) return;
    
    // Validate read batch size
    if (changedFields.globalReadBatchSize !== undefined) {
      const batchSize = Number(changedFields.globalReadBatchSize);
      if (isNaN(batchSize) || batchSize <= 1) {
        setReadBatchSizeError('Read batch size must be greater than 1');
        showNotification('Please fix the read batch size before saving', 'error');
        return;
      }
    }

    // Validate read delay millis
    if (changedFields.globalReadDelayMillis !== undefined) {
      const delayMillis = Number(changedFields.globalReadDelayMillis);
      if (isNaN(delayMillis) || delayMillis <= 10) {
        setReadDelayError('Read delay must be greater than 10 milliseconds');
        showNotification('Please fix the read delay before saving', 'error');
        return;
      }
    }
    
    // Validate artificial delay before saving
    if (changedFields.artificialDelaySetting) {
      const value = changedFields.artificialDelaySetting;
      // Trim whitespace from the value
      const trimmedValue = value.trim();
      
      // Check if empty
      if (!trimmedValue) {
        setArtificialDelayError('Value cannot be empty');
        showNotification('Please fix the artificial delay format before saving', 'error');
        return;
      }

      const parts = trimmedValue.split(',').map(part => part.trim());
      if (parts.length !== 3) {
        setArtificialDelayError('Format must be: enabled,minDelay,maxDelay');
        showNotification('Please fix the artificial delay format before saving', 'error');
        return;
      }

      const [enabled, minDelay, maxDelay] = parts;
      
      // Validate enabled flag
      if (enabled.toLowerCase() !== 'true' && enabled.toLowerCase() !== 'false') {
        setArtificialDelayError('First value must be true or false');
        showNotification('Please fix the artificial delay format before saving', 'error');
        return;
      }

      // Validate minDelay
      const minDelayNum = parseInt(minDelay);
      if (isNaN(minDelayNum) || minDelayNum < 0) {
        setArtificialDelayError('minDelay must be a positive number');
        showNotification('Please fix the artificial delay format before saving', 'error');
        return;
      }

      // Validate maxDelay
      const maxDelayNum = parseInt(maxDelay);
      if (isNaN(maxDelayNum) || maxDelayNum < 0) {
        setArtificialDelayError('maxDelay must be a positive number');
        showNotification('Please fix the artificial delay format before saving', 'error');
        return;
      }

      // Validate maxDelay >= minDelay
      if (maxDelayNum < minDelayNum) {
        setArtificialDelayError('maxDelay must be greater than or equal to minDelay');
        showNotification('Please fix the artificial delay format before saving', 'error');
        return;
      }

      setArtificialDelayError(null);
    }
    
    setIsSaving(true);
    setError(null);
    
    try {
      console.log('Saving configuration with changes:', changedFields);
      await apiService.postConfig({
        globalSettings: changedFields
      });
      console.log('Configuration saved successfully');
      
      await fetchConfig();
      setHasChanges(false);
      setChangedFields({});
      setArtificialDelayError(null);
      setReadBatchSizeError(null);
      setReadDelayError(null);
      showNotification('Configuration saved successfully', 'success');
    } catch (error) {
      console.error('Save error:', error);
      setError('Failed to save configuration. Please try again.');
      showNotification('Failed to save configuration', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isSaving) return;
    fetchConfig();
    setChangedFields({});
    setHasChanges(false);
    setArtificialDelayError(null);
    setReadBatchSizeError(null);
    setReadDelayError(null);
    showNotification('Changes cancelled', 'success');
  };

  const isFieldChanged = (field: keyof GlobalSettingsType) => {
    return field in changedFields;
  };

  if (error) {
    return (
      <div className="error-message">
        <h3>Error</h3>
        <p>{error}</p>
        <button onClick={fetchConfig} className="button primary">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="loading-message">
        <h3>Loading Configuration...</h3>
        <p>Please wait while we fetch the settings...</p>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="error-message">
        <h3>No Configuration Available</h3>
        <p>Unable to load configuration. Please try again.</p>
        <button onClick={fetchConfig} className="button primary">
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}
      
      <div className="settings-container">
        <h2>Global Settings</h2>
        {readOnlyExceptLogLevel ? (
          <p className="sync-readonly-hint">Secondary node — only log level can be changed.</p>
        ) : null}
        
        <div className="settings-grid">
          <div className={`form-group ${isFieldChanged('globalReadBatchSize') ? 'changed' : ''}`}>
            <label htmlFor="globalReadBatchSize">Global Read Batch Size</label>
            <input
              id="globalReadBatchSize"
              type="number"
              value={settings.globalReadBatchSize}
              onChange={handleChange('globalReadBatchSize')}
              disabled={isSaving || readOnlyExceptLogLevel}
              className={readBatchSizeError ? 'error' : ''}
              min="2"
            />
            <small>Must be greater than 1</small>
            {readBatchSizeError && (
              <div className="error-text">{readBatchSizeError}</div>
            )}
          </div>

          <div className={`form-group ${isFieldChanged('globalReadDelayMillis') ? 'changed' : ''}`}>
            <label htmlFor="globalReadDelayMillis">Global Read Delay (ms)</label>
            <input
              id="globalReadDelayMillis"
              type="number"
              value={settings.globalReadDelayMillis}
              onChange={handleChange('globalReadDelayMillis')}
              disabled={isSaving || readOnlyExceptLogLevel}
              className={readDelayError ? 'error' : ''}
              min="11"
            />
            <small>Must be greater than 10 milliseconds</small>
            {readDelayError && (
              <div className="error-text">{readDelayError}</div>
            )}
          </div>

          <div className={`form-group full-width ${isFieldChanged('globalPostURL') ? 'changed' : ''}`}>
            <label htmlFor="globalPostURL">Global Post URL</label>
            <input
              id="globalPostURL"
              type="text"
              value={settings.globalPostURL}
              onChange={handleChange('globalPostURL')}
              disabled={isSaving || readOnlyExceptLogLevel}
            />
          </div>

          <div className={`form-group ${isFieldChanged('logLevel') ? 'changed' : ''}`}>
            <label htmlFor="logLevel">Log Level</label>
            <select
              id="logLevel"
              value={settings.logLevel}
              onChange={handleChange('logLevel')}
              disabled={isSaving}
            >
              <option value="ERROR">ERROR</option>
              <option value="WARN">WARN</option>
              <option value="INFO">INFO</option>
              <option value="DEBUG">DEBUG</option>
              <option value="TRACE">TRACE</option>
            </select>
          </div>

          <div className={`form-group ${isFieldChanged('artificialDelaySetting') ? 'changed' : ''}`}>
            <label htmlFor="artificialDelaySetting">Artificial Delay Setting</label>
            <input
              id="artificialDelaySetting"
              type="text"
              value={settings.artificialDelaySetting}
              onChange={handleChange('artificialDelaySetting')}
              disabled={isSaving || readOnlyExceptLogLevel}
              className={artificialDelayError ? 'error' : ''}
              placeholder="true,100,200"
            />
            <small>Format: enabled,minDelay,maxDelay (e.g., true,100,200)</small>
            {artificialDelayError && (
              <div className="error-text">{artificialDelayError}</div>
            )}
          </div>
        </div>

        <div className="button-group">
          <button
            className="button secondary"
            onClick={handleCancel}
            disabled={!hasChanges || isSaving}
          >
            Cancel
          </button>
          <button
            className="button primary"
            onClick={handleSave}
            disabled={!hasChanges || isSaving || !!artificialDelayError || !!readBatchSizeError || !!readDelayError}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
}; 